import { useState, useContext, useCallback } from 'react';

import { AgentContext } from '../context-providers/AgentContext';
import { ModalController } from '../modals';
import AgentsListModal from '../modals/AgentsListModal';
import { warmTransferToAgent } from '../api';
import { CallWithDuration, Agent } from '../types';

export type TransferFeedback = {
  type: 'success' | 'error';
  message: string;
} | null;

type UseWarmTransferResult = {
  transferFeedback: TransferFeedback;
  handleWarmTransfer: () => void;
  clearFeedback: () => void;
};

const useWarmTransfer = (call: CallWithDuration | undefined): UseWarmTransferResult => {
  const { identity } = useContext(AgentContext);
  const [transferFeedback, setTransferFeedback] = useState<TransferFeedback>(null);

  const agentsModal = ModalController.useModal(AgentsListModal);

  const clearFeedback = useCallback(() => {
    setTransferFeedback(null);
  }, []);

  const handleWarmTransfer = useCallback(() => {
    if (!identity) {
      setTransferFeedback({ type: 'error', message: 'Agent identity not available' });
      return;
    }

    agentsModal.show({ excludeIdentity: identity }).then(async(result) => {
      const agent = result as Agent | undefined;
      if (agent && call) {
        try {
          setTransferFeedback(null);
          await warmTransferToAgent({
            agentIdentity: agent.identity,
            callSid: call.parameters.CallSid,
          });
          setTransferFeedback({
            type: 'success',
            message: `Agent ${agent.identity} was invited to the call`,
          });
        } catch (error) {
          console.error('Warm transfer failed:', error);
          setTransferFeedback({
            type: 'error',
            message: `Invitation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          });
        }
      }
    });
  }, [identity, agentsModal, call]);

  return { transferFeedback, handleWarmTransfer, clearFeedback };
};

export default useWarmTransfer;
